// src/utils/dataReset.ts
import { db } from '../config/firebase';
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
  writeBatch,
  limit,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Collections to clear
const COLLECTIONS_TO_CLEAR = [
  'leads',
  'appointments',
  'walkLists',
  'workloads',
  'surveys',
  'surveyResponses',
  'workloadAssignmentLogs',
  'communications',
  'notifications',
  'mail',
  'userGroups',
  'externalTagUploads',
] as const;

// Subcollections to clear (format: 'parentCollection/*/subCollection')
const SUBCOLLECTIONS_TO_CLEAR = [
  { parent: 'workloads', sub: 'addresses' },
] as const;

export interface ResetProgress {
  collection: string;
  total: number;
  deleted: number;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
  error?: string;
}

export interface ResetResult {
  success: boolean;
  collectionsCleared: string[];
  documentsDeleted: number;
  usersDeleted: number;
  errors: { collection: string; error: string }[];
  duration: number;
}

/**
 * Delete all documents in a collection using batched writes
 */
async function clearCollection(
  collectionName: string,
  onProgress?: (progress: ResetProgress) => void
): Promise<{ deleted: number; error?: string }> {
  let totalDeleted = 0;
  const batchSize = 500;

  try {
    let hasMore = true;

    while (hasMore) {
      // Get a batch of documents
      const q = query(collection(db, collectionName), limit(batchSize));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        hasMore = false;
        break;
      }

      // Create a batch delete
      const batch = writeBatch(db);
      snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      totalDeleted += snapshot.size;

      onProgress?.({
        collection: collectionName,
        total: totalDeleted,
        deleted: totalDeleted,
        status: 'in_progress',
      });

      // If we got fewer than batchSize, we're done
      if (snapshot.size < batchSize) {
        hasMore = false;
      }
    }

    onProgress?.({
      collection: collectionName,
      total: totalDeleted,
      deleted: totalDeleted,
      status: 'completed',
    });

    return { deleted: totalDeleted };
  } catch (error: any) {
    const errorMsg = error.message || 'Unknown error';
    onProgress?.({
      collection: collectionName,
      total: totalDeleted,
      deleted: totalDeleted,
      status: 'error',
      error: errorMsg,
    });
    return { deleted: totalDeleted, error: errorMsg };
  }
}

/**
 * Clear subcollections within parent documents
 */
async function clearSubcollections(
  parentCollection: string,
  subCollection: string,
  onProgress?: (progress: ResetProgress) => void
): Promise<{ deleted: number; error?: string }> {
  let totalDeleted = 0;
  const collectionPath = `${parentCollection}/*/${subCollection}`;

  try {
    // First, get all parent documents
    const parentDocs = await getDocs(collection(db, parentCollection));

    for (const parentDoc of parentDocs.docs) {
      // Get all subcollection documents for this parent
      const subQuery = collection(db, parentCollection, parentDoc.id, subCollection);
      const subDocs = await getDocs(subQuery);

      if (subDocs.empty) continue;

      // Batch delete subcollection documents
      const batchSize = 500;
      let batch = writeBatch(db);
      let batchCount = 0;

      for (const subDoc of subDocs.docs) {
        batch.delete(subDoc.ref);
        batchCount++;

        if (batchCount >= batchSize) {
          await batch.commit();
          totalDeleted += batchCount;
          batch = writeBatch(db);
          batchCount = 0;

          onProgress?.({
            collection: collectionPath,
            total: totalDeleted,
            deleted: totalDeleted,
            status: 'in_progress',
          });
        }
      }

      // Commit any remaining
      if (batchCount > 0) {
        await batch.commit();
        totalDeleted += batchCount;
      }
    }

    onProgress?.({
      collection: collectionPath,
      total: totalDeleted,
      deleted: totalDeleted,
      status: 'completed',
    });

    return { deleted: totalDeleted };
  } catch (error: any) {
    const errorMsg = error.message || 'Unknown error';
    onProgress?.({
      collection: collectionPath,
      total: totalDeleted,
      deleted: totalDeleted,
      status: 'error',
      error: errorMsg,
    });
    return { deleted: totalDeleted, error: errorMsg };
  }
}

/**
 * Delete test users (all users except the admin performing the reset)
 * This requires a Cloud Function since client SDK can't delete Auth users
 */
async function deleteTestUsers(
  adminUserId: string,
  onProgress?: (message: string) => void
): Promise<{ deleted: number; error?: string }> {
  try {
    onProgress?.('Fetching user list...');

    // Get all user documents from Firestore
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const userIdsToDelete = usersSnapshot.docs
      .filter((doc) => doc.id !== adminUserId)
      .map((doc) => doc.id);

    if (userIdsToDelete.length === 0) {
      onProgress?.('No test users to delete');
      return { deleted: 0 };
    }

    onProgress?.(`Found ${userIdsToDelete.length} users to delete`);

    // Call Cloud Function to delete Auth users
    // Note: This function needs to be created in Cloud Functions
    const functions = getFunctions();
    const deleteUsers = httpsCallable<{ userIds: string[] }, { deleted: number }>(
      functions,
      'deleteUsers'
    );

    try {
      const result = await deleteUsers({ userIds: userIdsToDelete });
      onProgress?.(`Deleted ${result.data.deleted} Auth users`);
    } catch (error) {
      // If Cloud Function doesn't exist yet, just delete Firestore docs
      console.warn('deleteUsers Cloud Function not available, skipping Auth deletion');
      onProgress?.('Auth user deletion skipped (Cloud Function not deployed)');
    }

    // Delete user documents from Firestore
    const batch = writeBatch(db);
    let batchCount = 0;
    let totalDeleted = 0;

    for (const userId of userIdsToDelete) {
      batch.delete(doc(db, 'users', userId));
      batchCount++;

      if (batchCount >= 500) {
        await batch.commit();
        totalDeleted += batchCount;
        batchCount = 0;
        onProgress?.(`Deleted ${totalDeleted} user documents...`);
      }
    }

    if (batchCount > 0) {
      await batch.commit();
      totalDeleted += batchCount;
    }

    onProgress?.(`Completed: ${totalDeleted} users deleted`);
    return { deleted: totalDeleted };
  } catch (error: any) {
    return { deleted: 0, error: error.message || 'Failed to delete users' };
  }
}

/**
 * Main reset function - clears all test data and users
 */
export async function resetAppData(
  adminUserId: string,
  options: {
    deleteUsers?: boolean;
    collectionsToSkip?: string[];
    onProgress?: (progress: ResetProgress | string) => void;
  } = {}
): Promise<ResetResult> {
  const { deleteUsers: shouldDeleteUsers = true, collectionsToSkip = [], onProgress } = options;
  const startTime = Date.now();

  const result: ResetResult = {
    success: true,
    collectionsCleared: [],
    documentsDeleted: 0,
    usersDeleted: 0,
    errors: [],
    duration: 0,
  };

  try {
    // 1. Clear subcollections first (before parent documents)
    for (const { parent, sub } of SUBCOLLECTIONS_TO_CLEAR) {
      if (collectionsToSkip.includes(`${parent}/${sub}`)) continue;

      onProgress?.({
        collection: `${parent}/*/${sub}`,
        total: 0,
        deleted: 0,
        status: 'pending',
      });

      const subResult = await clearSubcollections(parent, sub, (p) => {
        if (typeof onProgress === 'function') {
          onProgress(p);
        }
      });

      if (subResult.error) {
        result.errors.push({ collection: `${parent}/*/${sub}`, error: subResult.error });
      } else {
        result.collectionsCleared.push(`${parent}/*/${sub}`);
      }
      result.documentsDeleted += subResult.deleted;
    }

    // 2. Clear main collections
    for (const collectionName of COLLECTIONS_TO_CLEAR) {
      if (collectionsToSkip.includes(collectionName)) continue;

      onProgress?.({
        collection: collectionName,
        total: 0,
        deleted: 0,
        status: 'pending',
      });

      const collResult = await clearCollection(collectionName, (p) => {
        if (typeof onProgress === 'function') {
          onProgress(p);
        }
      });

      if (collResult.error) {
        result.errors.push({ collection: collectionName, error: collResult.error });
      } else {
        result.collectionsCleared.push(collectionName);
      }
      result.documentsDeleted += collResult.deleted;
    }

    // 3. Delete test users if requested
    if (shouldDeleteUsers) {
      onProgress?.('Deleting test users...');
      const userResult = await deleteTestUsers(adminUserId, (msg) => {
        if (typeof onProgress === 'function') {
          onProgress(msg);
        }
      });

      if (userResult.error) {
        result.errors.push({ collection: 'users', error: userResult.error });
      }
      result.usersDeleted = userResult.deleted;
    }

    result.success = result.errors.length === 0;
  } catch (error: any) {
    result.success = false;
    result.errors.push({ collection: 'general', error: error.message || 'Unknown error' });
  }

  result.duration = Date.now() - startTime;
  return result;
}

/**
 * Preview what will be deleted without actually deleting
 */
export async function previewReset(
  adminUserId: string,
  collectionsToSkip: string[] = []
): Promise<{
  collections: { name: string; count: number }[];
  users: number;
  total: number;
}> {
  const collections: { name: string; count: number }[] = [];
  let totalCount = 0;

  // Count documents in each collection
  for (const collectionName of COLLECTIONS_TO_CLEAR) {
    if (collectionsToSkip.includes(collectionName)) continue;

    try {
      const snapshot = await getDocs(collection(db, collectionName));
      const count = snapshot.size;
      collections.push({ name: collectionName, count });
      totalCount += count;
    } catch (error) {
      collections.push({ name: collectionName, count: -1 }); // -1 indicates error
    }
  }

  // Count users (excluding admin)
  let userCount = 0;
  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    userCount = usersSnapshot.docs.filter((doc) => doc.id !== adminUserId).length;
  } catch (error) {
    console.error('Error counting users:', error);
  }

  return {
    collections,
    users: userCount,
    total: totalCount + userCount,
  };
}

/**
 * Check if user has admin permissions to reset data
 */
export async function canResetData(userId: string): Promise<boolean> {
  try {
    const userDoc = await getDocs(
      query(collection(db, 'users'), where('__name__', '==', userId))
    );

    if (userDoc.empty) return false;

    const userData = userDoc.docs[0].data();
    return userData.role === 'provider_admin';
  } catch (error) {
    console.error('Error checking reset permissions:', error);
    return false;
  }
}
